import { useCallback, useEffect } from "react";
import {
  Box,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup,
  FormErrorMessage,
} from "@avenue-8/sidekick-ui";
import { Controller, useFormContext } from "react-hook-form";
import { PropertyTypeTreeDto } from "@shared/apis/property-search/generated";
import { FindListingsFiltersFormSchema } from "@cma/chat-widgets/find-listings-search/helpers/property-search-filters-form-helpers";
import styled from "@emotion/styled";

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

interface Props {
  propertyTypes: PropertyTypeTreeDto["propertyTypes"];
}

export const DealTypeField = (props: Props) => {
  const { propertyTypes } = props;
  const { control, getValues, setValue } =
    useFormContext<FindListingsFiltersFormSchema>();

  const calculateDealTypeFromPropertyTypes = useCallback(
    (propertyTypeIds: string[] | null | undefined): "Sale" | "Lease" => {
      if (
        !propertyTypeIds ||
        propertyTypeIds.length === 0 ||
        propertyTypes.length === 0
      ) {
        return "Sale";
      }

      const dealTypes = new Set<string>();

      propertyTypeIds.forEach((id) => {
        const parentType = propertyTypes.find((pt) => pt.id === id);
        if (parentType?.dealType) {
          dealTypes.add(parentType.dealType);
          return;
        }

        for (const propertyType of propertyTypes) {
          const subType = propertyType.subTypes?.find((st) => st.id === id);
          if (subType && propertyType.dealType) {
            dealTypes.add(propertyType.dealType);
            return;
          }
        }
      });

      const result = dealTypes.size === 1 ? Array.from(dealTypes)[0] : "Sale";
      return result === "Lease" ? "Lease" : "Sale";
    },
    [propertyTypes]
  );

  useEffect(() => {
    const initialPropertyTypes = getValues("propertyTypes");
    const initialDealType =
      calculateDealTypeFromPropertyTypes(initialPropertyTypes);
    calculateDealTypeFromPropertyTypes(initialPropertyTypes);
    setValue("dealType", initialDealType);
  }, [calculateDealTypeFromPropertyTypes, getValues, setValue]);

  return (
    <Container>
      <FormLabel>Deal Type</FormLabel>
      <Controller
        name="dealType"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <>
            <ToggleButtonGroup
              {...field}
              exclusive
              value={field.value}
              onChange={(_, value) => {
                if (value !== null) {
                  field.onChange(value);
                }
              }}
              fullWidth
            >
              <ToggleButton value="Sale" size="small">
                Sale
              </ToggleButton>
              <ToggleButton value="Lease" size="small">
                Lease
              </ToggleButton>
            </ToggleButtonGroup>
            {error && <FormErrorMessage>{error.message}</FormErrorMessage>}
          </>
        )}
      />
    </Container>
  );
};
